import { useTheme } from "@emotion/react";
import dayjs from "dayjs";
import { Box, Stack, Typography } from "@mui/material";
import {
  DownloadOutlined, PublishOutlined, BoltOutlined, FlashOnOutlined,
  ThermostatOutlined, TimelineOutlined, VpnLockOutlined,
  NetworkPing, SpeedOutlined, TrendingDownOutlined, StorageOutlined,
  DeviceThermostatOutlined,
} from "@mui/icons-material";
import CardGraphComponent from "../cardGraphComponent";
import CardDataComponent from "../overview/cardDataComponent";
import LineChartCard from "../dashOnuComponent/lineChartCard";
import StatusComponent from "../statusComponent";
import MultiMetricGraphComponent from "../saude/multiMetricGraphComponent";
import {
  RENDERER_DEFAULTS, resolveColor, resolveChip,
  getProgressValue as calcProgress, FORMATTERS,
} from "../../utils/metricsRenderer";

const utc = require("dayjs/plugin/utc");
const timezone = require("dayjs/plugin/timezone");
dayjs.extend(utc);
dayjs.extend(timezone);
const TIMEZONE = "America/Sao_Paulo";

// Mapeamento de strings do schema para componentes de icones
const ICON_MAP = {
  DownloadOutlined, PublishOutlined, BoltOutlined, FlashOnOutlined,
  ThermostatOutlined, TimelineOutlined, VpnLockOutlined,
  NetworkPing, SpeedOutlined, TrendingDownOutlined, StorageOutlined,
  DeviceThermostatOutlined,
};

export default function MetricRenderer({
  metricKey, config, data, loading,
  handleAplicarZoom, isZoomEnabled, variant,
}) {
  const theme = useTheme();
  const defaults = RENDERER_DEFAULTS?.[config?.type] ?? {};
  const currentValue = data?.currentValue;
  const historyData = data?.data ?? [];
  const color = resolveColor(currentValue, config);
  const chip = resolveChip(currentValue, config);
  const unit = config?.unit ?? "";
  
  // Resolve o formatador com base no preset do schema ou padrao do tipo
  const formatFn = FORMATTERS?.[config?.formatPreset] ?? defaults?.formatValue;
  const formattedValue = formatFn
    ? formatFn(currentValue, unit)
    : currentValue ?? " - ";

  // Renderizacao de status do tipo statusChip usando o StatusComponent visualmente identico
  if (config?.type === "statusChip") {
    const isUp = Number(currentValue) === 1 || currentValue === true || String(currentValue)?.toLowerCase() === "up";
    return (
      <StatusComponent
        status={isUp}
        loading={loading}
      />
    );
  }

  // Renderizacao do grafico de multiplas metricas do tipo multiLineChart
  if (config?.type === "multiLineChart") {
    // Só passa formatValue quando há formatPreset explícito; assim métricas
    // existentes (WAN, CPU, RAM) mantêm a formatação padrão do componente.
    const multiFormat = config?.formatPreset
      ? FORMATTERS?.[config?.formatPreset]
      : undefined;
    return (
      <MultiMetricGraphComponent
        data={historyData || {}}
        dataNow={currentValue}
        titleGraph={config?.title ?? ""}
        unit={unit}
        loading={loading}
        handleAplicarZoom={handleAplicarZoom}
        isZoomEnabled={isZoomEnabled}
        // multiplica valores antes de exibir (ex: s → ms para itens Zabbix em segundos)
        valueMultiplier={config?.valueMultiplier ?? 1}
        hideProgress={config?.hideProgress ?? false}
        itemNames={data?.itemNames}
        labelExtractor={config?.labelExtractor}
        formatValue={multiFormat}
        hideStats={config?.hideStats ?? false}
        chartDomain={config?.chartDomain}
        chartTicks={config?.chartTicks}
      />
    );
  }

  // ── renderizacao de card (CardDataComponent) ──
  if (variant === "card") {
    return (
      <CardDataComponent
        cardTile={config?.title}
        cardValue={formattedValue}
        typeIcon={config?.typeIcon}
        colorCard={config?.colorCard}
        loading={loading}
      />
    );
  }

  // ── lineChart → LineChartCard ──
  if (config?.type === "lineChart") {
    return (
      <LineChartCard
        title={config?.title}
        icon={ICON_MAP[config?.icon]}
        loading={loading}
        getColor={() => color}
        currentValue={currentValue}
        chipColor={chip?.color}
        chipLabel={chip?.label}
        showChipStatus={!!chip}
        height="200px"
        data={historyData}
        maxValuebps={config?.range?.max}
        minValuebps={config?.range?.min}
        mainLineColor={theme?.palette?.cardGraphic?.graphLineColor?.[color]}
        mainColor={theme?.palette?.cardGraphic?.graphColor?.[color]}
        yOptions={{
          tickFormatter: (value) => {
            const v = formatFn ? formatFn(value, unit) : value;
            return [v, unit].filter(Boolean).join(" ");
          },
        }}
        xOptions={{
          tickFormatter: (clock) =>
            dayjs?.unix(clock)?.tz(TIMEZONE)?.format("DD/MM HH:mm"),
        }}
        customTooltip={(props) => (
          <MetricTooltip
            {...props}
            valueUnit={unit}
            title={config?.title}
            formatValue={formatFn}
            color={color}
          />
        )}
        handleAplicarZoom={handleAplicarZoom}
        isZoomEnabled={isZoomEnabled}
      />
    );
  }

  // ── demais tipos graficos → CardGraphComponent ──
  const hideProgress = defaults?.hideProgress ?? false;

  return (
    <CardGraphComponent
      data={historyData}
      titleGraph={config?.title}
      loading={loading}
      icon={ICON_MAP[config?.icon]}
      valueCard={formattedValue}
      hideProgress={hideProgress}
      iconColorCard={color}
      getProgressValue={
        !hideProgress && config?.progressRange
          ? () => calcProgress(currentValue, config?.progressRange)
          : null
      }
      tickFormatter={(value) => {
        const v = formatFn ? formatFn(value, unit) : value;
        return [v, unit].filter(Boolean).join(" ");
      }}
      CustomTooltip={(props) => (
        <MetricTooltip
          {...props}
          valueUnit={unit}
          title={config?.title}
          formatValue={formatFn}
          color={color}
        />
      )}
      handleAplicarZoom={handleAplicarZoom}
      isZoomEnabled={isZoomEnabled}
    />
  );
}

function MetricTooltip({
  active, payload, label, valueUnit, title, formatValue, color,
}) {
  const theme = useTheme();
  if (!active || !payload?.length) return null;
  const { clock, value } = payload?.[0]?.payload ?? {};
  const formatted = formatValue
    ? formatValue(value, valueUnit)
    : value?.toFixed(2);
  return (
    <Box
      sx={{
        background: theme?.palette?.background?.paper,
        p: 1, borderRadius: 1, boxShadow: 3,
      }}
    >
      <Typography fontSize={14}>
        {dayjs?.unix(clock)?.format("DD/MM/YY HH:mm")}
      </Typography>
      <Stack direction="row" alignItems="center" gap="4px">
        <Box
          sx={{
            width: 12, height: 12, borderRadius: "50%",
            bgcolor: theme?.palette?.cardGraphic?.graphColor?.[color],
          }}
        />
        <Typography fontSize={14}>
          {title}: {formatted} {valueUnit}
        </Typography>
      </Stack>
    </Box>
  );
}
